import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Activity } from '../../api/types';

function selectedTargets(activities: Activity[], ids: string[]) {
  return ids.map(id => {
    const row = activities.find(activity => activity.id === id);
    return {
      id,
      title: row?.title ?? id,
      repoName: row?.repoName ?? row?.azureRepository ?? null,
    };
  });
}

export function usePortfolioSelection(activities: Activity[]) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const eligibleActivityIds = useMemo(
    () =>
      activities
        .filter(activity => activity.status !== 'completed')
        .map(activity => activity.id),
    [activities],
  );
  const selectedEligibleCount = eligibleActivityIds.filter(id =>
    selectedIds.includes(id),
  ).length;
  const allEligibleSelected =
    eligibleActivityIds.length > 0 &&
    selectedEligibleCount === eligibleActivityIds.length;

  useEffect(() => {
    setSelectedIds(previous => {
      const visible = previous.filter(id => eligibleActivityIds.includes(id));
      return visible.length === previous.length ? previous : visible;
    });
  }, [eligibleActivityIds]);

  const clear = useCallback(() => setSelectedIds([]), []);
  const selectAll = (checked: boolean) =>
    setSelectedIds(checked ? eligibleActivityIds : []);
  const select = (id: string, checked: boolean) =>
    setSelectedIds(previous =>
      checked
        ? [...new Set([...previous, id])]
        : previous.filter(value => value !== id),
    );
  return {
    selectedIds,
    selectedTargets: selectedTargets(activities, selectedIds),
    eligibleActivityIds,
    selectedEligibleCount,
    allEligibleSelected,
    clear,
    selectAll,
    select,
  };
}
export type PortfolioSelection = ReturnType<typeof usePortfolioSelection>;
